import { env } from "@/env";
import { delayBy } from "@/fp";
import { RestCallsKey, callResolver } from "@/mock/demo/rest.resolver";
import { Ref, ref } from "@vue/composition-api";
import { UseLoading } from "../composition/loading";
import { AxiosError } from "axios";

export interface ApiSuccessResult<R> {
  success: true;
  value: R;
}

export interface ApiFailureResult {
  success: false;
  code: number;
  body?: any;
}

export type ApiResult<R> = ApiSuccessResult<R> | ApiFailureResult;

export interface MockRestCall {
  delay?: number;
  status?: number;
  payload: any;
}
interface MockRestApiPlan {
  delay: number;
  active: boolean;
  calls: Record<RestCallsKey, MockRestCall>;
}

interface ApiCallExtraProps {
  memo?: { lastResultsCount: number; getKey: (arg: any) => string };
  force_mock?: () => boolean;
}

const create_rest_api_client = () => {
  let mock_plan: MockRestApiPlan;
  const create_mock_plan = (plan: MockRestApiPlan) => {
    mock_plan = plan;
    // @ts-ignore
    window._rest_mock_plan = mock_plan;
  };
  // @ts-ignore
  window.create_rest_mock_plan = create_mock_plan;
  // @ts-ignore
  window.inject_mock_api = (calls: Record<RestCallsKey, MockRestCall>) => {
    if (mock_plan) {
      mock_plan.calls = {
        ...mock_plan.calls,
        ...calls,
      };
    }
  };

  const error_body = (e: AxiosError): any | null => {
    if (e && e.response && e.response.data) {
      return e.response.data;
    } else {
      return null;
    }
  };

  const use_mock_plan = (key: RestCallsKey) =>
    mock_plan && mock_plan.active && mock_plan.calls[key];

  const apiCall = <ARGS, T>(
    callKey: RestCallsKey,
    happyPath: (args: ARGS) => Promise<ApiSuccessResult<T>>,
    extra_props: ApiCallExtraProps = {}
  ) => {
    const { force_mock = () => false, memo } = extra_props;
    const memoMap = new Map();
    const keys: string[] = [];
    return async (a: ARGS): Promise<ApiResult<T>> => {
      if (use_mock_plan(callKey) || force_mock()) {
        console.log("REST MOCK for tag :", callKey);
        const { payload, delay, status } = mock_plan.calls[callKey];
        await delayBy(delay || mock_plan.delay);
        if (!status) {
          return {
            success: true,
            value: await payload(a),
          };
        } else {
          return {
            success: false,
            // @ts-ignore
            code: status,
          };
        }
      }
      if (a && memo && memoMap.has(memo.getKey(a))) {
        return {
          success: true,
          value: memoMap.get(memo.getKey(a)),
        };
      }
      try {
        const _env = await env();
        if (_env.env === "demo") {
          const res = await callResolver<ARGS, T>({
            type: callKey,
            payload: a,
          });
          return res;
        } else {
          const res = await happyPath(a);
          if (memo) {
            if (keys.length === memo.lastResultsCount) {
              const idToRemove = keys.shift();
              memoMap.delete(idToRemove);
            }
            const key = memo.getKey(a);
            keys.push(key);
            memoMap.set(key, res.value);
          }
          return res;
        }
      } catch (e) {
        // @ts-ignore
        console.log("e.response::", e);
        return {
          success: false,
          body: error_body(e as AxiosError),
          // @ts-ignore
          code: e ? (e.response ? e.response.status : 0) : 0,
        };
      }
    };
  };
  return { apiCall, create_mock_plan };
};

export const make_export_csv_api_call = async (
  url: string,
  file_name: string
): Promise<boolean> => {
  try {
    const { SharedState } = await import("@/common/state");
    const res = await fetch(`${SharedState.env.value.server}${url}`, {
      headers: {
        authorization: SharedState.loggedUser.value.authorization,
        accept: "text/csv",
      },
    });
    const blob = await res.blob();
    const download_url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = download_url;
    a.download = file_name;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(download_url);
    a.remove();
    return true;
  } catch (e) {
    return false;
  }
};

export const { apiCall, create_mock_plan } = create_rest_api_client();

export const UseApiCall = <ARGS, T>(
  callKey: RestCallsKey,
  happyPath: (args: ARGS) => Promise<ApiSuccessResult<T>>,
  extra_props: ApiCallExtraProps = {}
) => {
  return (
    seed: T | null
  ): [Ref<T>, Ref<boolean>, (a: ARGS) => Promise<ApiResult<T>>] => {
    const [is_loading, set_loading] = UseLoading(false).as_arr();
    const data: Ref<T> = ref(seed);
    const api_call = apiCall<ARGS, T>(callKey, happyPath, extra_props);

    const exec = async (a: ARGS) => {
      set_loading(true);
      const res = await api_call(a);
      if (res.success) {
        data.value = res.value;
      }
      set_loading(false);
      return res;
    };
    return [data, is_loading, exec];
  };
};
